import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CheckField } from '../Form';
import { formatDateTime } from '../../utils/format';
import { monthName } from '../../pages/Master/data/gtcApi';
import {
  articleLabel,
  buildAmendSummary,
  clauseLabel,
  countAmended,
  formatAmendDate,
  sortByOrder,
} from './gtcAmendSummary';
import '../../pages/Master/components/GtcDetailView.css';
import './GtcViewer.css';

export const NO_AMEND_TEXT =
  'No Amendment Proposed (Comply with Buyer GTC Clause)';

export const clauseAnchorId = clauseId => `gtc-clause-${clauseId}`;

// Scroll to a clause anchor and flash it. Runs after the expand re-render.
export const scrollToClause = clauseId => {
  setTimeout(() => {
    const el = document.getElementById(clauseAnchorId(clauseId));
    if (!el) return;
    if (el.scrollIntoView)
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('gtcv-flash');
    setTimeout(() => el.classList.remove('gtcv-flash'), 2000);
  }, 60);
};

const AmendBadge = ({ count }) =>
  count > 0 ? (
    <span className='gtcv-badge gtcv-badge--amend'>{count} amended</span>
  ) : null;

AmendBadge.propTypes = { count: PropTypes.number };

// Counter + clickable positions + "only amended" filter (fe#123 §4).
// showPositions=false hides the chip list (Offer / Clarification: too long).
export const GtcAmendSummaryPanel = ({
  summary,
  onJump,
  onlyAmended,
  onToggleOnly,
  showPositions = true,
}) => (
  <div className='gtcv-summary'>
    <div className='gtcv-summary__head'>
      <span className='gtcv-summary__count'>
        <i className='ri-edit-2-line' aria-hidden='true' /> Amended Clauses:{' '}
        <strong>{summary.total}</strong> of {summary.totalClauses}
      </span>
      {summary.perGtc.length > 1 &&
        summary.perGtc.map(g => (
          <span key={g.gtcId} className='gtcv-badge'>
            {g.gtcNumber}: {g.total}/{g.totalClauses}
          </span>
        ))}
      {onToggleOnly && summary.total > 0 && (
        <CheckField
          name='gtcv-only-amended'
          switchStyle
          label='Show amended only'
          checked={onlyAmended}
          onChange={e => onToggleOnly(e.target.checked)}
          className='gtcv-summary__filter mb-0'
        />
      )}
    </div>
    {summary.total === 0 ? (
      <div className='gtcv-summary__none'>
        No amendment — the supplier complies with all buyer GTC clauses.
      </div>
    ) : showPositions ? (
      <div className='gtcv-summary__positions'>
        {summary.positions.map(p => (
          <button
            key={p.clauseId}
            type='button'
            className='gtcv-chip'
            onClick={() => onJump(p)}
          >
            <i className='ri-map-pin-line' aria-hidden='true' /> {p.path}
          </button>
        ))}
      </div>
    ) : null}
  </div>
);

GtcAmendSummaryPanel.propTypes = {
  summary: PropTypes.object.isRequired,
  onJump: PropTypes.func.isRequired,
  onlyAmended: PropTypes.bool,
  onToggleOnly: PropTypes.func,
  showPositions: PropTypes.bool,
};

// Read-only GTC tree: GTC > category > article > clause.
// `renderAmend(clause, ctx)` (optional) replaces the read-only amendment
// column, used by Master Amend GTC detail for its editor.
const GtcViewer = ({
  gtcs = [],
  showAmend = false,
  emptyText = 'No GTC selected.',
  source,
  snapshotDate,
  renderAmend,
  showPositions = true,
}) => {
  const list = useMemo(() => sortByOrder(gtcs || []), [gtcs]);
  const summary = useMemo(() => buildAmendSummary(list), [list]);
  // GTC level starts open; deeper levels open on click / jump.
  const [expanded, setExpanded] = useState(() => new Set());
  const [collapsedGtc, setCollapsedGtc] = useState(() => new Set());
  const [onlyAmended, setOnlyAmended] = useState(false);

  const toggle = id =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleGtc = id =>
    setCollapsedGtc(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const jump = p => {
    setCollapsedGtc(prev => {
      const next = new Set(prev);
      next.delete(p.gtcId);
      return next;
    });
    setExpanded(prev => new Set([...prev, p.categoryId, p.articleId]));
    scrollToClause(p.clauseId);
  };

  const filtering = showAmend && onlyAmended && summary.total > 0;
  const isOpen = id => filtering || expanded.has(id);

  if (!list.length) {
    return <div className='gtcd-empty-inline'>{emptyText}</div>;
  }

  const renderClause = (gtc, category, article, clause) => {
    if (filtering && !clause.isAmend) return null;
    const amended = showAmend && clause.isAmend;
    return (
      <div
        key={clause.id}
        id={clauseAnchorId(clause.id)}
        className={`gtcv-clause${amended ? ' gtcv-clause--amended' : ''}`}
      >
        <div className='gtcv-clause__head'>
          <span className='gtcv-clause__number'>{clauseLabel(clause)}</span>
          {amended && (
            <span className='gtcv-badge gtcv-badge--amend'>AMENDED</span>
          )}
        </div>
        {showAmend ? (
          <div className='gtcv-clause__cols'>
            <div>
              <div className='gtcv-label'>GTC Clause from Buyer</div>
              <div className='gtcv-text'>{clause.content || '-'}</div>
            </div>
            <div>
              {renderAmend ? (
                renderAmend(clause, { gtc, category, article })
              ) : (
                <>
                  <div className='gtcv-label'>
                    Is Amend: {clause.isAmend ? 'ON' : 'OFF'}
                  </div>
                  <div className='gtcv-text'>
                    {clause.isAmend ? clause.amendDetail || '-' : NO_AMEND_TEXT}
                  </div>
                  {clause.isAmend && clause.amendedAt && (
                    <div className='gtcv-saved'>
                      Saved {formatAmendDate(clause.amendedAt)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div className='gtcv-text'>{clause.content || '-'}</div>
        )}
      </div>
    );
  };

  const renderArticle = (gtc, category, article) => {
    const count = countAmended(article);
    if (filtering && !count) return null;
    const open = isOpen(article.id);
    const clauses = sortByOrder(article.clauses);
    return (
      <div key={article.id} className='gtcd-group gtcd-group--article'>
        {/* Whole row toggles; chevron clicks bubble up to it. */}
        <div
          className='gtcd-node gtcd-node--article gtcv-node--toggle'
          onClick={() => toggle(article.id)}
        >
          <button
            type='button'
            className='gtcd-node__chevron'
            aria-expanded={open}
            aria-label={open ? 'Collapse article' : 'Expand article'}
          >
            <i
              className={
                open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
              }
              aria-hidden='true'
            />
          </button>
          <span className='gtcd-node__label gtcv-static'>
            {articleLabel(article)}
          </span>
          {showAmend && <AmendBadge count={count} />}
          <span className='gtcd-node__meta'>{clauses.length} clause</span>
        </div>
        {open && (
          <div className='gtcd-children'>
            {clauses.length === 0 ? (
              <div className='gtcd-empty-inline'>
                No clauses in this article yet.
              </div>
            ) : (
              clauses.map(c => renderClause(gtc, category, article, c))
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCategory = (gtc, category) => {
    const count = countAmended(category);
    if (filtering && !count) return null;
    const open = isOpen(category.id);
    const articles = sortByOrder(category.articles);
    return (
      <div key={category.id} className='gtcd-group gtcd-group--category'>
        <div
          className='gtcd-node gtcd-node--category gtcv-node--toggle'
          onClick={() => toggle(category.id)}
        >
          <button
            type='button'
            className='gtcd-node__chevron'
            aria-expanded={open}
            aria-label={open ? 'Collapse category' : 'Expand category'}
          >
            <i
              className={
                open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
              }
              aria-hidden='true'
            />
          </button>
          <span className='gtcd-node__label gtcv-static'>{category.name}</span>
          {showAmend && <AmendBadge count={count} />}
          <span className='gtcd-node__meta'>{articles.length} article</span>
        </div>
        {open && (
          <div className='gtcd-children'>
            {articles.length === 0 ? (
              <div className='gtcd-empty-inline'>
                No articles in this category yet.
              </div>
            ) : (
              articles.map(a => renderArticle(gtc, category, a))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='gtcv'>
      {source === 'snapshot' && (
        <div className='gtcv-source'>
          <i className='ri-camera-line' aria-hidden='true' /> Snapshot taken
          when the offer was submitted on {formatDateTime(snapshotDate)}
        </div>
      )}
      {source === 'live' && showAmend && (
        <div className='gtcv-source'>
          <i className='ri-information-line' aria-hidden='true' /> Amendments
          are taken from Master Amend GTC
        </div>
      )}
      {showAmend && (
        <GtcAmendSummaryPanel
          summary={summary}
          onJump={jump}
          onlyAmended={onlyAmended}
          onToggleOnly={setOnlyAmended}
          showPositions={showPositions}
        />
      )}
      <div className='gtcd-tree gtcv-tree'>
        {list.map(gtc => {
          const open = filtering || !collapsedGtc.has(gtc.id);
          const categories = sortByOrder(gtc.categories);
          const count = countAmended(gtc);
          return (
            <div key={gtc.id} className='gtcv-gtc'>
              <button
                type='button'
                className='gtcv-gtc__head'
                aria-expanded={open}
                onClick={() => toggleGtc(gtc.id)}
              >
                <i
                  className={
                    open ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
                  }
                  aria-hidden='true'
                />
                <span className='gtcv-gtc__number'>{gtc.gtcNumber}</span>
                <span className='gtcv-gtc__title'>{gtc.title}</span>
                <span className='gtcv-gtc__meta'>
                  {[monthName(gtc.month), gtc.year].filter(Boolean).join(' ')}
                  {gtc.incotermName ? ` · ${gtc.incotermName}` : ''}
                </span>
                {showAmend && <AmendBadge count={count} />}
              </button>
              {open && (
                <div className='gtcv-gtc__body'>
                  {categories.length === 0 ? (
                    <div className='gtcd-empty-inline'>
                      No categories or clauses defined for this GTC document.
                    </div>
                  ) : (
                    categories.map(c => renderCategory(gtc, c))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

GtcViewer.propTypes = {
  gtcs: PropTypes.array,
  showAmend: PropTypes.bool,
  emptyText: PropTypes.node,
  source: PropTypes.string,
  snapshotDate: PropTypes.string,
  renderAmend: PropTypes.func,
  showPositions: PropTypes.bool,
};

export default GtcViewer;
